/**
 * NEON COACH - موجه الصفحات (Single Page Application Hash Router)
 * يتعامل بسلاسة مع التبديل بين الشاشات مع حفظ الحالة وتحديث شريط التنقل
 */

import { renderHeader, bindHeaderEvents } from '../components/header.js';
import { renderBottomNav } from '../components/navBar.js';

import { renderQuestionnaireView, bindQuestionnaireEvents } from '../views/questionnaireView.js';
import { renderTodayView, bindTodayViewEvents } from '../views/todayView.js';
import { renderFortyDayWorkoutView, bindFortyDayWorkoutEvents } from '../views/fortyDayWorkoutView.js';
import { renderWorkoutSessionView, bindWorkoutSessionEvents } from '../views/workoutSessionView.js';
import { renderNutritionView, bindNutritionEvents } from '../views/nutritionView.js';
import { renderMealLogView, bindMealLogEvents } from '../views/mealLogView.js';
import { renderWaterSuppsView, bindWaterSuppsEvents } from '../views/waterSuppsView.js';
import { renderWeeklyCheckinView, bindWeeklyCheckinEvents } from '../views/weeklyCheckinView.js';
import { renderProgressReportView, bindProgressReportEvents } from '../views/progressReportView.js';
import { renderRecipesView, bindRecipesEvents } from '../views/recipesView.js';
import { renderCoachDashboardView, bindCoachDashboardEvents } from '../views/coachDashboardView.js';
import { renderProfileView, bindProfileEvents } from '../views/profileView.js';

import { renderMetabolismView, bindMetabolismEvents } from '../views/metabolismView.js';

import { store } from '../state/store.js';
import { authService } from '../services/authService.js';
import { syncService } from '../services/syncService.js';
import { initCrossTabActionToastListener } from '../services/actionToastService.js';
import { clearWorkoutPageEffects } from '../utils/workoutPageEffects.js';

export const ROUTES = {
  metabolism: { render: renderMetabolismView, bind: bindMetabolismEvents, showNav: true, showHeader: true },
  questionnaire: { render: renderQuestionnaireView, bind: bindQuestionnaireEvents, showNav: false, showHeader: false },
  today: { render: renderTodayView, bind: bindTodayViewEvents, showNav: true, showHeader: true },
  workout: { render: renderFortyDayWorkoutView, bind: bindFortyDayWorkoutEvents, showNav: true, showHeader: true },
  'workout-session': { render: renderWorkoutSessionView, bind: bindWorkoutSessionEvents, showNav: false, showHeader: false },
  nutrition: { render: renderNutritionView, bind: bindNutritionEvents, showNav: true, showHeader: true },
  'meal-log': { render: renderMealLogView, bind: bindMealLogEvents, showNav: false, showHeader: true },
  'water-supps': { render: renderWaterSuppsView, bind: bindWaterSuppsEvents, showNav: true, showHeader: true },
  checkin: { render: renderWeeklyCheckinView, bind: bindWeeklyCheckinEvents, showNav: true, showHeader: true },
  recipes: { render: renderRecipesView, bind: bindRecipesEvents, showNav: true, showHeader: true },
  progress: { render: renderProgressReportView, bind: bindProgressReportEvents, showNav: true, showHeader: true },
  coach: { render: renderCoachDashboardView, bind: bindCoachDashboardEvents, showNav: true, showHeader: true },
  profile: { render: renderProfileView, bind: bindProfileEvents, showNav: true, showHeader: true }
};

// تعطيل استعادة المتصفح التلقائية لموضع التمرير لضمان بدء كل تابة دائماً من أعلى الصفحة
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

export class Router {
  constructor(appElement) {
    this.appElement = appElement;
    this.currentRoute = '';
    window.addEventListener('hashchange', () => this.handleRoute());
    
    // معالج عام لإغلاق أي نافذة منبثقة عند الضغط على زر الإغلاق ✕ أو النقر خارج النافذة
    document.addEventListener('click', (e) => {
      const modalClose = e.target.closest('[data-action="close"], [aria-label="إغلاق"], [id^="close-"], .close-custom-food-modal-btn, .close-btn, .btn-close');
      if (modalClose) {
        e.preventDefault();
        e.stopPropagation();
        const modal = modalClose.closest('.ai-modal-overlay, .modal');
        if (modal) {
          modal.classList.remove('open', 'is-open');
          return;
        }
      }

      if (e.target.classList && (e.target.classList.contains('ai-modal-overlay') || e.target.classList.contains('modal'))) {
        e.target.classList.remove('open', 'is-open');
        return;
      }

      const navTarget = e.target.closest('a[href^="#"], .nav-item, .client-tab-btn, .ws-view-tab, .auth-tab-btn');
      if (navTarget && !navTarget.closest('.ai-modal-overlay, .modal')) {
        this.scrollToTop();
      }
    });

    // إغلاق أي نافذة منبثقة بمفتاح Escape
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        document.querySelectorAll('.ai-modal-overlay.open, .modal.open, .modal.is-open').forEach(m => m.classList.remove('open', 'is-open'));
      }
    });

    // إعادة رسم الشاشة تلقائياً عند تغير الحالة المركزية
    store.subscribe(() => {
      this.refreshCurrentView();
    });

    // إذا كانت الشاشة الحالية هي شاشة الاستبيان وتم تأكيد اكتماله من المزامنة المحلية/السحابية، التحويل فوراً لليوم
    store.subscribe((state) => {
      const profile = state?.userProfile;
      const done = profile?.onboardingCompleted || profile?.onboarding_completed;
      if (done && this.currentRoute === 'questionnaire') {
        window.location.hash = '#today';
      }
    });

    // الاستماع لإجراءات العميل الصوتي وتنبيه المستخدم عند تعديل تابة أخرى
    initCrossTabActionToastListener(() => this.currentRoute);
  }

  init() {
    // 1. فتح فوري بدون أي تأخير اعتماداً على البيانات المخزنة محلياً في الذاكرة (Offline-First / Cache-First)
    this.checkInitialAccess();

    // 2. مزامنة البيانات السحابية في الخلفية دون تعطيل واجهة المستخدم أو إبطاء شاشة البداية
    this.syncInBackground();
  }

  async syncInBackground() {
    try {
      await authService.whenAuthReady();
      const effectiveUserId = authService.getEffectiveUserId?.();
      if (effectiveUserId) {
        await syncService.loadUserData(effectiveUserId);
      }
    } catch (err) {
      console.warn('Background sync error:', err);
    }
  }

  checkInitialAccess() {
    const hash = window.location.hash || '';
    const profile = store.getState()?.userProfile;
    const hasCompletedOnboarding = profile?.onboardingCompleted || profile?.onboarding_completed;
    const rawHash = hash.replace(/^#\/?/, '').split('?')[0];
    const routeKey = rawHash.split('/')[0];

    if (!hasCompletedOnboarding) {
      if (routeKey !== 'questionnaire') {
        window.location.hash = '#questionnaire';
        return;
      }
    } else {
      if (!rawHash) {
        window.location.hash = '#today';
        return;
      }
    }

    this.handleRoute();
  }

  handleRoute() {
    const hash = window.location.hash || '';
    const rawHash = hash.replace(/^#\/?/, '') || '';
    const routePath = rawHash.split('?')[0];
    const routeKey = routePath.split('/')[0];

    const profile = store.getState()?.userProfile;
    const hasCompletedOnboarding = profile?.onboardingCompleted || profile?.onboarding_completed;

    // توجيه المستخدم الجديد إلى شاشة الأسئلة
    if (!hasCompletedOnboarding && routeKey !== 'questionnaire') {
      window.location.hash = '#questionnaire';
      return;
    }

    // دعم الرابط القديم داخل موجه التطبيق دون فتح صفحة HTML مستقلة
    if (routeKey === 'forty-day') {
      window.location.hash = hasCompletedOnboarding ? '#workout' : '#questionnaire';
      return;
    }

    if (routeKey === 'neon-ai') {
      window.location.hash = hasCompletedOnboarding ? '#today' : '#questionnaire';
      return;
    }

    if (routeKey === 'shopping-list' || routeKey === 'shopping') {
      window.location.hash = hasCompletedOnboarding ? '#nutrition' : '#questionnaire';
      return;
    }

    const defaultRoute = hasCompletedOnboarding ? 'today' : 'questionnaire';
    const effectiveKey = ROUTES[routeKey] ? routeKey : defaultRoute;
    const route = ROUTES[effectiveKey];
    this.currentRoute = effectiveKey;

    this.renderRoute(route, effectiveKey);
  }

  scrollToTop() {
    const performScroll = () => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      } catch (_) {
        window.scrollTo(0, 0);
      }
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      const container = document.getElementById('view-container');
      if (container) container.scrollTop = 0;
      if (this.appElement) this.appElement.scrollTop = 0;
      const appRoot = document.getElementById('app');
      if (appRoot) appRoot.scrollTop = 0;
    };

    // Only correct the next paint; delayed forced scrolling on each route
    // could pull a user's first swipe back to the top on other tabs.
    performScroll();
    requestAnimationFrame(performScroll);
  }

  renderRoute(route, routeKey) {
    try {
      this.cleanupView?.();
    } catch (error) {
      // A view cleanup must never trap the user on the current tab.
      console.error('Failed to clean up the previous route:', error);
    } finally {
      this.cleanupView = null;
    }
    // Route boundaries must never inherit an old training modal/drag scroll
    // lock. An invisible fixed overlay or body overflow:hidden blocks all
    // scrolling in unrelated tabs, especially on iOS standalone PWAs.
    document.querySelectorAll('body > .ai-modal-overlay').forEach(el => el.remove());
    clearWorkoutPageEffects();

    let html = '';

    if (route.showHeader) {
      html += renderHeader();
    }

    html += `<main id="view-container" class="view-fade-slide" style="flex: 1;">${route.render()}</main>`;

    if (route.showNav) {
      html += renderBottomNav(routeKey);
    }

    this.appElement.innerHTML = html;

    // بدء الشاشة فوراً من أعلى الصفحة قبل وبعد ربط الأحداث
    this.scrollToTop();

    // ربط الأحداث
    if (route.showHeader) bindHeaderEvents();
    if (route.bind) this.cleanupView = route.bind();

    this.scrollToTop();
  }

  refreshCurrentView() {
    // Keep the active command/card and drawer mounted while the store is refreshed.
    if (this.currentRoute === 'workout') return;
    // تنظيف أي نوافذ منبثقة ملحقة بـ body مباشرة قبل إعادة الرسم
    document.querySelectorAll('body > .ai-modal-overlay').forEach(el => el.remove());

    const route = ROUTES[this.currentRoute] || ROUTES['today'];
    const container = document.getElementById('view-container');
    if (container) {
      this.cleanupView?.();
      this.cleanupView = null;
      container.innerHTML = route.render();
      if (route.bind) this.cleanupView = route.bind();
    }
  }
}
